import React, { ReactNode } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  SportsScore as SportsIcon,
  Leaderboard as LeaderboardIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  ManageAccounts as ManageAccountsIcon,
  AdminPanelSettings as AdminPanelIcon,
  Euro as EuroIcon,
  Gavel as GavelIcon,
  EmojiEvents as TrophyIcon,
  Campaign as CampaignIcon,
  Warning as WarningIcon,
  Block as BlockedIpsIcon,
  SwapHoriz as TransferIcon,
  CalendarToday as CalendarIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  SportsHandball as CourtIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Calculate as CalculateIcon,
  MilitaryTech as BadgeIcon,
  LocalOffer as LocalOfferIcon,
  HelpOutline as FaqIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { apiRequest } from "../config/api";
import { useEffect } from "react";
import splLogo from "../img/SPL_Logo.png";
import padelEagleLogo from "../img/padelEagle.png";
import SessionTimer from "./SessionTimer";
import { AdminNavigationPreferences } from "../types";

interface LayoutProps {
  children: ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [navPreferences, setNavPreferences] = useState<AdminNavigationPreferences | null>(null);
  const { user, logout } = useAuth();
  const { currentOrg } = useOrganization();
  const { mode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchPendingRequestsCount();
    fetchNavigationPreferences();
  }, []);

  const fetchPendingRequestsCount = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        "/api/admin/scorekeeper-requests/count",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setPendingRequestsCount(data.count);
      }
    } catch (error) {
      console.error("Error fetching pending requests count:", error);
    }
  };

  const fetchNavigationPreferences = async () => {
    try {
      const response = await apiRequest("/api/admin-preferences/navigation");
      
      if (response.ok) {
        const prefs: AdminNavigationPreferences = await response.json();
        setNavPreferences(prefs);
      }
    } catch (error) {
      console.error("Error fetching navigation preferences:", error);
      // Silently fail - will use default order
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate("/"); // Redirect to public home page instead of login
  };

  // Determine organization-specific theme
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const orgColor = isPadelEagle ? "#743444" : "#1976d2";
  const orgColorLight = isPadelEagle ? "#743444" : "#42a5f5";
  const orgAccent = isPadelEagle ? "#E49421" : "#e3f2fd";
  // Priority: custom uploaded logo > hardcoded logo
  const orgLogo =
    currentOrg?.branding?.logo || (isPadelEagle ? padelEagleLogo : splLogo);
  const orgName = isPadelEagle
    ? "Padel Eagle"
    : currentOrg?.name || "League Admin";

  // Feature-based menu items - adapt based on organization type
  const isLeagueOrg =
    currentOrg?.competitionType === "league" ||
    currentOrg?.competitionType === "both";
  const isTournamentOrg =
    currentOrg?.competitionType === "tournament" ||
    currentOrg?.competitionType === "both";

  const menuItems = [
    { text: "Home", icon: <HomeIcon />, path: "/" },
    { text: "Dashboard", icon: <DashboardIcon />, path: "/admin/dashboard" },
    { text: "Players", icon: <PeopleIcon />, path: "/admin/players" },

    // League-specific features
    ...(isLeagueOrg
      ? [
          {
            text: "League Days",
            icon: <EventIcon />,
            path: "/admin/league-days",
          },
        ]
      : []),

    // Tournament-specific features
    ...(isTournamentOrg
      ? [
          {
            text: "Tournaments",
            icon: <TrophyIcon />,
            path: "/admin/tournaments",
          },
        ]
      : []),

    { text: "Rankings", icon: <LeaderboardIcon />, path: "/admin/rankings" },

    // League-specific features
    ...(isLeagueOrg
      ? [
          { text: "Finances", icon: <EuroIcon />, path: "/admin/finances" },
          { text: "Financial Calculator", icon: <CalculateIcon />, path: "/admin/financial-calculator" },
          {
            text: "Penalties & Bonuses",
            icon: <GavelIcon />,
            path: "/admin/penalties",
          },
          {
            text: "Nations League",
            icon: <TrophyIcon />,
            path: "/admin/nations-league",
          },
        ]
      : []),

    {
      text: "Announcements",
      icon: <CampaignIcon />,
      path: "/admin/announcements",
    },
    {
      text: "FAQ Settings",
      icon: <FaqIcon />,
      path: "/admin/faq-settings",
    },
    {
      text: "Venues",
      icon: <PlaceIcon />,
      path: "/admin/venue-settings",
    },
    {
      text: "Scorekeeper Requests",
      icon: <NotificationsIcon />,
      path: "/admin/scorekeeper-requests",
    },
    {
      text: "Manage Scorekeepers",
      icon: <ManageAccountsIcon />,
      path: "/admin/scorekeepers",
    },
    {
      text: "Player Accounts",
      icon: <PeopleIcon />,
      path: "/admin/player-accounts",
    },
    {
      text: "Deletion Requests",
      icon: <DeleteIcon />,
      path: "/admin/deletion-requests",
    },
    {
      text: "Tournament Registrations",
      icon: <PeopleIcon />,
      path: "/admin/tournament-registration-requests",
    },
    {
      text: "Admin Users",
      icon: <AdminPanelIcon />,
      path: "/admin/admins",
    },
    {
      text: "Seasons",
      icon: <CalendarIcon />,
      path: "/admin/seasons",
    },
    {
      text: "Court Management",
      icon: <CourtIcon />,
      path: "/admin/court-management",
    },
    {
      text: "Email Settings",
      icon: <EmailIcon />,
      path: "/admin/email-settings",
    },
    {
      text: "Season Registrations",
      icon: <PersonAddIcon />,
      path: "/admin/season-registrations",
    },
    {
      text: "Discount Codes",
      icon: <LocalOfferIcon />,
      path: "/admin/discount-codes",
    },
    {
      text: "Transfers",
      icon: <TransferIcon />,
      path: "/admin/transfers",
    },
    {
      text: "Navigation Settings",
      icon: <SettingsIcon />,
      path: "/admin/navigation-settings",
    },
    {
      text: "Badge Settings",
      icon: <BadgeIcon />,
      path: "/admin/badge-settings",
    },
    {
      text: "Player Badges",
      icon: <BadgeIcon />,
      path: "/admin/player-badges",
    },
    {
      text: "Privacy Policy",
      icon: <SettingsIcon />,
      path: "/admin/privacy-policy-settings",
    },
    {
      text: "Blocked IPs",
      icon: <BlockedIpsIcon />,
      path: "/admin/blocked-ips",
    },
    {
      text: "Dangerous",
      icon: <WarningIcon />,
      path: "/admin/dangerous",
    },
  ];

  // Apply navigation preferences (custom order and hidden items) and permissions filtering
  const displayedMenuItems = React.useMemo(() => {
    let items = menuItems;

    // Filter by admin permissions (if not super admin)
    if (user && user.role === 'admin' && !user.isSuperAdmin && user.permissions) {
      // Super admins see everything, regular admins only see permitted tabs
      items = menuItems.filter((item) => user.permissions!.includes(item.text));
    }

    if (!navPreferences || !navPreferences.menuOrder || navPreferences.menuOrder.length === 0) {
      // No preferences - use default order
      return items;
    }

    // Filter out hidden items
    const visibleItems = items.filter(
      (item) => !navPreferences.hiddenItems.includes(item.text)
    );

    // Apply custom order
    const orderedItems = [...visibleItems].sort((a, b) => {
      const indexA = navPreferences.menuOrder.indexOf(a.text);
      const indexB = navPreferences.menuOrder.indexOf(b.text);

      // If item is not in custom order, put it at the end
      const orderA = indexA === -1 ? 9999 : indexA;
      const orderB = indexB === -1 ? 9999 : indexB;

      return orderA - orderB;
    });

    return orderedItems;
  }, [menuItems, navPreferences, user]);

  const drawer = (
    <Box sx={{ width: 240 }}>
      <Toolbar>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={orgLogo}
            alt={orgName}
            style={{
              height: isPadelEagle ? "40px" : "32px",
              width: "auto",
              borderRadius: "4px",
              border: "none",
            }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontSize: "0.9rem",
              color: isPadelEagle ? "#743444" : "inherit",
              fontWeight: isPadelEagle ? "bold" : "normal",
            }}
          >
            {orgName}
          </Typography>
        </Box>
      </Toolbar>
      <List>
        {displayedMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
                // Refresh count when navigating to scorekeeper requests
                if (item.path === "/admin/scorekeeper-requests") {
                  setTimeout(fetchPendingRequestsCount, 500);
                }
              }}
            >
              <ListItemIcon>
                {item.text === "Scorekeeper Requests" &&
                pendingRequestsCount > 0 ? (
                  <Badge badgeContent={pendingRequestsCount} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: isPadelEagle
            ? "#743444"
            : undefined,
          boxShadow: isPadelEagle
            ? "none"
            : undefined,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}
          >
            <img
              src={orgLogo}
              alt={orgName}
              style={{
                height: isPadelEagle ? "36px" : "28px",
                width: "auto",
                borderRadius: isPadelEagle ? "8px" : "4px",
                border: "none",
                ...(isPadelEagle && { background: "#ffffff", padding: "3px" }),
              }}
            />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                display: { xs: "none", md: "block" },
                fontWeight: isPadelEagle ? "bold" : "normal",
                background: isPadelEagle
                  ? "none"
                  : undefined,
                backgroundClip: isPadelEagle ? "text" : undefined,
                WebkitBackgroundClip: isPadelEagle ? "text" : undefined,
                WebkitTextFillColor: isPadelEagle ? "#fff" : undefined,
              }}
            >
              {orgName} - Admin Panel
            </Typography>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                display: { xs: "block", md: "none" },
                fontWeight: isPadelEagle ? "bold" : "normal",
                background: isPadelEagle
                  ? "none"
                  : undefined,
                backgroundClip: isPadelEagle ? "text" : undefined,
                WebkitBackgroundClip: isPadelEagle ? "text" : undefined,
                WebkitTextFillColor: isPadelEagle ? "#fff" : undefined,
              }}
            >
              {isPadelEagle ? "🦅 PE" : "🎾 SPL"}
            </Typography>
          </Box>
          <SessionTimer compact />
          <Typography
            variant="body2"
            sx={{
              mx: 2,
              display: { xs: "none", sm: "block" },
            }}
          >
            Welcome, {user?.username}
          </Typography>
          {!isPadelEagle && (
<IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
            {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
)}
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              minWidth: { xs: "auto", sm: "64px" },
              px: { xs: 1, sm: 2 },
              ...(isPadelEagle && {
                color: "#ffffff",
                backgroundColor: "transparent",
                border: "1px solid #ffffff",
                "&:hover": { backgroundColor: "#E49421", borderColor: "#E49421", color: "#ffffff" },
                "& .MuiButton-startIcon, & span": { color: "#ffffff" },
              }),
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              Logout
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: "100%",
          mt: 8,
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            width: "100%",
            mx: "auto",
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
}

export default Layout;
